import { Link2, Loader2, QrCode } from 'lucide-react';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useNavigate } from 'react-router-dom';
import urlService from '../api/urlService';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../components/ui/alert.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';

interface UrlResponse {
  id: string;
  shortCode: string;
  originalUrl: string;
  shortUrl: string;
  qrCodeUrl: string;
}

const CreateUrl = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<UrlResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(null);

    if (!url) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      setLoading(true);
      const response = await urlService.shortenUrl(url, customSlug);
      setSuccess(response);
      setUrl('');
      setCustomSlug('');
    } catch (err: any) {
      setError(err.message || 'Failed to create shortened URL');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="container py-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Create Shortened URL</h1>
            <p className="text-muted-foreground">
              Enter a long URL to create a shortened version and QR code
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Shorten URL</CardTitle>
              <CardDescription>
                Create a shortened URL that's easy to share and track
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="url">URL to shorten</Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com/very/long/url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customSlug">
                    Custom slug (optional)
                  </Label>
                  <Input
                    id="customSlug"
                    placeholder="my-custom-url"
                    value={customSlug}
                    onChange={(e) => setCustomSlug(e.target.value)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Leave empty for an auto-generated slug
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Link2 className="mr-2 h-4 w-4" />
                      Create Shortened URL
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Shortened URL Created
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Shortened URL</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={success.shortUrl}
                      className="font-mono"
                    />
                    <CopyToClipboard
                      text={success.shortUrl}
                      onCopy={handleCopy}
                    >
                      <Button variant="outline">
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </CopyToClipboard>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>QR Code</Label>
                  <div className="flex flex-col items-center gap-4">
                    <div className="border rounded-lg p-4 bg-background">
                      <img
                        src={success.qrCodeUrl}
                        alt="QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = success.qrCodeUrl;
                        link.download = `qrcode-${success.shortCode}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <QrCode className="mr-2 h-4 w-4" />
                      Download QR Code
                    </Button>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/urls/${success.shortCode}`)}
                >
                  View Statistics
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateUrl;
